/**
 * WorkflowTree — one submission and every branch it grew.
 *
 * Desktop: pedigree layout. The root's steps run down the left; each step
 * branches right into the sub-tasks it started (2.1, 3.1, 3.1.1…), with
 * dashed cards for sub-tasks that start at a later step.
 *
 * Phone: the same tree as an indented list, folded to what matters —
 * finished branches and upcoming steps collapse, overdue work is pulled
 * into a "Needs attention" strip, and tapping a branch zooms into it so a
 * deep tree never indents more than two levels on screen.
 *
 * Data comes from GET /api/workflow-items/:id/tree (server/services/workflowTree.js).
 */
import React, { useMemo, useState } from 'react';
import { useIsMobile } from '../../mobile';
import { parseUTC, formatDate } from '../../utils/dateUtils';

const C = {
  ink: '#111827', soft: '#4b5563', muted: '#9ca3af', line: '#cbd5e1', border: '#e5e7eb',
  done: '#059669', doneBg: '#d1fae5', cur: '#3F66A0', curBg: '#eef3fb',
  okBg: '#dbeafe', okFg: '#1e40af', soonBg: '#fef3c7', soonFg: '#92400e', badBg: '#fee2e2', badFg: '#991b1b',
};

const CSS = `
.wft-srow{display:flex;align-items:flex-start;position:relative;padding-bottom:8px}
.wft-srow::before{content:'';position:absolute;left:8px;top:0;bottom:0;border-left:2px solid ${C.border}}
.wft-srow:first-child::before{top:24px}.wft-srow:last-child::before{bottom:auto;height:24px}.wft-srow:only-child::before{display:none}
.wft-kids{position:relative;padding-left:16px;display:flex;flex-direction:column}
.wft-kids::before{content:'';position:absolute;left:-14px;top:24px;width:30px;border-top:1.5px solid ${C.line}}
.wft-kid{position:relative;padding:0 0 10px 16px;display:flex;align-items:flex-start}
.wft-kid:last-child{padding-bottom:0}
.wft-kid::before{content:'';position:absolute;left:0;top:24px;width:16px;border-top:1.5px solid ${C.line}}
.wft-kid::after{content:'';position:absolute;left:0;top:0;bottom:0;border-left:1.5px solid ${C.line}}
.wft-kid:first-child::after{top:24px}.wft-kid:last-child::after{bottom:auto;height:24px}.wft-kid:only-child::after{display:none}
.wft-card{transition:border-color .15s,box-shadow .15s}
.wft-card.real:hover{border-color:${C.cur};box-shadow:0 2px 8px rgba(63,102,160,.12)}
`;

// ── helpers ────────────────────────────────────────────────────────────────

const shortName = (n) => {
  if (!n) return '';
  const parts = n.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
};

const fmtDur = (ms) => {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr${hrs === 1 ? '' : 's'}`;
  const days = Math.round(hrs / 24);
  return `${days} days`;
};

// Handler titles like "X — from Y #12" are noise in a tree; the workflow
// name says it better.
const displayTitle = (n) => (n.title && !/ — from .*#\d+$/.test(n.title) ? n.title : n.workflow_name);

// done / overdue / soon / ok / none, with a short and a long label.
function dueState(node) {
  if (node.completed_at) return { kind: 'done', short: 'Done', long: `Done ${formatDate(node.completed_at, { month: 'short', day: 'numeric' })}` };
  const d = parseUTC(node.sla_deadline);
  if (!d || Number.isNaN(d.getTime())) return { kind: 'none', short: '', long: '' };
  const diff = d.getTime() - Date.now();
  if (diff < 0) return { kind: 'bad', short: 'Overdue', long: `Overdue ${fmtDur(-diff)}` };
  if (diff < 24 * 3600 * 1000) return { kind: 'soon', short: fmtDur(diff), long: `Due in ${fmtDur(diff)}` };
  const day = formatDate(node.sla_deadline, { month: 'short', day: 'numeric' });
  return { kind: 'ok', short: day, long: `Due ${day}` };
}

const badgeStyle = (kind) => ({
  done: { background: C.doneBg, color: '#065f46' },
  bad: { background: C.badBg, color: C.badFg },
  soon: { background: C.soonBg, color: C.soonFg },
  ok: { background: C.okBg, color: C.okFg },
}[kind] || null);

function Badge({ kind, children }) {
  const s = badgeStyle(kind);
  if (!s || !children) return null;
  return (
    <span style={{ ...s, fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', lineHeight: 1.7 }}>
      {children}
    </span>
  );
}

function HoldTag({ node, parent }) {
  if (!parent) return null;
  if (node.hold_stage_id) {
    const st = (parent.stages || []).find(s => s.id === node.hold_stage_id);
    const what = !st ? 'next step' : parent.number ? `“${st.name}”` : `step ${st.step}`;
    return (
      <span title="The main path waits for this" style={{
        fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', lineHeight: 1.7,
        border: `1px solid ${C.line}`, color: C.soft,
      }}>
        🔒 Holds {what}
      </span>
    );
  }
  return (
    <span title="Runs on its own — never slows the main path" style={{
      fontSize: 11, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', lineHeight: 1.7,
      border: `1px dashed ${C.line}`, color: C.muted,
    }}>
      Independent
    </span>
  );
}

// Current step of a node (or its finish when done).
const currentStep = (n) => (n.stages || []).find(s => s.id === n.current_stage_id);

// Main-path rows: working steps in order, then ONE finish row (the one
// reached, else the first) with the other endings listed under it.
function mainPath(node) {
  const stages = node.stages || [];
  const working = stages.filter(s => s.stage_type !== 'terminal');
  const finishes = stages.filter(s => s.stage_type === 'terminal');
  const finish = finishes.find(s => s.status === 'done') || finishes[0] || null;
  const others = finishes.filter(s => s !== finish).map(s => s.name);
  return { rows: finish ? [...working, finish] : working, finish, others };
}

// Which row a child hangs off. Falls back to the finish row / first row.
function rowFor(child, rows) {
  const direct = rows.find(r => r.id === child.spawned_at_stage_id);
  return direct ? direct.id : (rows[rows.length - 1] || {}).id;
}

// Unfinished real + planned Hold children blocking a given step.
function waitingOn(node, stageId) {
  return (node.children || [])
    .filter(c => c.hold_stage_id === stageId && (c.planned || !c.completed_at))
    .map(c => c.number);
}

function progress(node) {
  const { rows } = mainPath(node);
  const done = rows.filter(r => r.status === 'done').length;
  return { done, total: rows.length, pct: rows.length ? Math.round((done / rows.length) * 100) : 0 };
}

// Every real node, flattened (for Needs attention).
function flatten(node, out = [], parent = null) {
  out.push({ node, parent });
  for (const c of node.children || []) if (!c.planned) flatten(c, out, node);
  return out;
}

// True when a node and everything under it is finished.
const allDone = (n) => !!n.completed_at && (n.children || []).every(c => !c.planned && allDone(c));

function StepDot({ status, finish, size = 16 }) {
  const base = {
    display: 'block', width: size, height: size, flex: 'none', boxSizing: 'border-box', position: 'relative', zIndex: 1,
    borderRadius: finish ? 4 : '50%', background: '#fff', border: `2px solid ${C.line}`,
  };
  if (status === 'done') Object.assign(base, { background: C.done, borderColor: C.done });
  if (status === 'current') Object.assign(base, { border: `4px solid ${C.cur}` });
  if (status === 'skipped') Object.assign(base, { borderStyle: 'dashed' });
  return <span style={base} />;
}

function MiniDots({ node }) {
  const { rows } = mainPath(node);
  return (
    <div style={{ display: 'flex', gap: 3, marginTop: 7 }} aria-hidden="true">
      {rows.map(r => (
        <i key={r.id} style={{
          width: 7, height: 7, borderRadius: '50%',
          background: r.status === 'done' ? C.done : r.status === 'current' ? C.cur : C.line,
        }} />
      ))}
    </div>
  );
}

// ── Desktop: pedigree ──────────────────────────────────────────────────────

function Card({ node, parent, focusId, onOpenItem }) {
  const planned = !!node.planned;
  const due = planned ? null : dueState(node);
  const cur = planned ? null : currentStep(node);
  const owner = cur && cur.owners && cur.owners[0] ? shortName(cur.owners[0]) : '';
  const startsAt = planned && parent ? (parent.stages || []).find(s => s.id === node.spawned_at_stage_id) : null;
  const isFocus = !planned && node.id === focusId;
  return (
    <div
      className={`wft-card${planned ? '' : ' real'}`}
      onClick={planned ? undefined : () => onOpenItem(node.id)}
      role={planned ? undefined : 'button'}
      tabIndex={planned ? undefined : 0}
      onKeyDown={planned ? undefined : (e) => { if (e.key === 'Enter') onOpenItem(node.id); }}
      title={planned ? undefined : `Open ${displayTitle(node)}`}
      style={{
        width: 212, flex: 'none', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
        background: planned ? 'transparent' : '#fff',
        border: planned ? `1.5px dashed ${C.line}` : `1px solid ${isFocus ? C.cur : C.border}`,
        boxShadow: isFocus ? `0 0 0 2px ${C.curBg}` : undefined,
        cursor: planned ? 'default' : 'pointer',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: planned ? C.soft : C.ink, lineHeight: 1.35 }}>
        <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: C.muted, marginRight: 5 }}>{node.number}</span>
        {displayTitle(node)}
      </div>
      <div style={{ fontSize: 12, color: C.soft, marginTop: 2, lineHeight: 1.4 }}>
        {planned
          ? (startsAt ? (parent.number ? `Starts at “${startsAt.name}”` : `Starts at step ${startsAt.step}`) : 'Starts later')
          : [cur?.name, owner].filter(Boolean).join(' · ')}
        {planned && node.condition && (
          <span style={{ color: C.muted }}> · only if {node.condition.field} {node.condition.operator === 'not_equals' ? '≠' : '='} {node.condition.value}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
        {due && <Badge kind={due.kind}>{due.long}</Badge>}
        <HoldTag node={node} parent={parent} />
        {isFocus && <Badge kind="ok">You're here</Badge>}
      </div>
      {!planned && <MiniDots node={node} />}
    </div>
  );
}

function Branch({ node, parent, focusId, onOpenItem }) {
  return (
    <div className="wft-kid">
      <Card node={node} parent={parent} focusId={focusId} onOpenItem={onOpenItem} />
      {(node.children || []).length > 0 && (
        <div className="wft-kids">
          {node.children.map(c => (
            <Branch key={c.planned ? `p${c.number}` : c.id} node={c} parent={node} focusId={focusId} onOpenItem={onOpenItem} />
          ))}
        </div>
      )}
    </div>
  );
}

function DesktopTree({ root, focusId, onOpenItem }) {
  const { rows, finish, others } = mainPath(root);
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ minWidth: 'max-content' }}>
        {rows.map(row => {
          const kids = (root.children || []).filter(c => rowFor(c, rows) === row.id);
          const waiting = waitingOn(root, row.id);
          const isCur = row.status === 'current';
          const faded = row.status === 'upcoming' || row.status === 'skipped';
          return (
            <div className="wft-srow" key={row.id}>
              <div style={{
                flex: 'none', display: 'flex', gap: 12, padding: '14px 10px 16px', boxSizing: 'border-box',
                marginLeft: isCur ? -10 : 0, paddingLeft: isCur ? 10 : 0, width: isCur ? 260 : 250,
                background: isCur ? C.curBg : undefined, borderRadius: 8,
              }}>
                <span style={{ marginTop: 3 }}><StepDot status={row.status} finish={row === finish} /></span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: faded ? C.soft : C.ink }}>
                    {row.step} · {row.name}
                  </div>
                  <div style={{ fontSize: 12, color: C.soft, marginTop: 1 }}>
                    {row.status === 'done' && row.entered_at && `Reached ${formatDate(row.entered_at, { month: 'short', day: 'numeric' })}`}
                    {row.status === 'current' && [
                      (row.owners || []).slice(0, 2).map(shortName).join(', '),
                      dueState(root).long,
                    ].filter(Boolean).join(' · ')}
                    {row.status === 'upcoming' && (row.sla_hours ? `${row.sla_hours} hr SLA` : 'Upcoming')}
                    {row.status === 'skipped' && 'Skipped'}
                  </div>
                  {waiting.length > 0 && row.status !== 'done' && (
                    <div style={{ fontSize: 12, color: C.soonFg, marginTop: 3 }}>🔒 Waiting on {waiting.join(', ')}</div>
                  )}
                  {row === finish && others.length > 0 && (
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>or {others.join(' · ')}</div>
                  )}
                </div>
              </div>
              {kids.length > 0 && (
                <div className="wft-kids">
                  {kids.map(c => (
                    <Branch key={c.planned ? `p${c.number}` : c.id} node={c} parent={root} focusId={focusId} onOpenItem={onOpenItem} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Phone: folded outline with zoom ────────────────────────────────────────

function PhoneRow({ node, parent, onZoom, emphasize }) {
  const planned = !!node.planned;
  const due = planned ? null : dueState(node);
  const canZoom = !planned;
  return (
    <button
      type="button"
      onClick={canZoom ? () => onZoom(node) : undefined}
      disabled={!canZoom}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44, padding: '6px 0',
        background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, textAlign: 'left',
        cursor: canZoom ? 'pointer' : 'default', color: planned ? C.muted : C.ink, fontSize: 14,
        fontWeight: emphasize ? 700 : 400,
      }}
    >
      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: C.muted, minWidth: 28 }}>{node.number}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {displayTitle(node)}{planned ? ' · later' : ''}
        {node.hold_stage_id && parent ? <span title="Holds a step" style={{ color: C.soft }}> 🔒</span> : null}
      </span>
      {due && due.kind !== 'none' && <Badge kind={due.kind}>{due.short}</Badge>}
      {canZoom && <span aria-hidden="true" style={{ color: C.muted, fontSize: 18 }}>›</span>}
    </button>
  );
}

function PhoneKids({ kids, parent, onZoom }) {
  if (!kids.length) return null;
  return (
    <div style={{ borderLeft: `1.5px solid ${C.line}`, marginLeft: 7, paddingLeft: 12 }}>
      {kids.map(c => {
        const grand = (c.children || []).length;
        return (
          <div key={c.planned ? `p${c.number}` : c.id}>
            <PhoneRow node={c} parent={parent} onZoom={onZoom} />
            {grand > 0 && (
              <div style={{ marginLeft: 36, fontSize: 12, color: C.muted, padding: '4px 0' }}>
                {grand} more sub-task{grand === 1 ? '' : 's'} inside ›
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PhoneTree({ root, focusId, onOpenItem }) {
  // Zoom path: array of nodes from root to the one on screen. A deep link
  // from a sub-task opens zoomed into that sub-task.
  const initialPath = useMemo(() => {
    const walk = (n, path) => {
      if (n.id === focusId) return path.concat(n);
      for (const c of n.children || []) {
        if (c.planned) continue;
        const found = walk(c, path.concat(n));
        if (found) return found;
      }
      return null;
    };
    return walk(root, []) || [root];
  }, [root, focusId]);
  const [path, setPath] = useState(initialPath);
  const [showDone, setShowDone] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const node = path[path.length - 1];
  const parent = path.length > 1 ? path[path.length - 2] : null;
  const zoom = (n) => setPath(prev => [...prev, n]);
  const zoomTo = (target) => {
    // From "Needs attention": rebuild the path from the root.
    const walk = (n, p) => {
      if (n === target) return p.concat(n);
      for (const c of n.children || []) { if (!c.planned) { const f = walk(c, p.concat(n)); if (f) return f; } }
      return null;
    };
    setPath(walk(root, []) || [root]);
  };

  const { rows, finish, others } = mainPath(node);
  const prog = progress(node);
  const kidsOf = (rowId) => (node.children || []).filter(c => rowFor(c, rows) === rowId);
  const curIdx = rows.findIndex(r => r.status === 'current');
  const doneRows = rows.filter((r, i) => r.status === 'done' && (curIdx < 0 || i < curIdx) && kidsOf(r.id).every(allDone));
  const upcomingRows = rows.filter((r, i) => curIdx >= 0 && i > curIdx && r.status !== 'done');
  const hidden = new Set([
    ...(showDone ? [] : doneRows.map(r => r.id)),
    ...(showUpcoming ? [] : upcomingRows.map(r => r.id)),
  ]);
  const upcomingKids = upcomingRows.reduce((n, r) => n + kidsOf(r.id).length, 0);

  const attention = path.length === 1
    ? flatten(root).filter(({ node: n }) => n !== root && ['bad', 'soon'].includes(dueState(n).kind))
    : [];

  return (
    <div>
      {path.length > 1 && (
        <button type="button" onClick={() => setPath(p => p.slice(0, -1))} style={{
          display: 'flex', alignItems: 'center', gap: 6, minHeight: 40, background: 'none', border: 'none',
          padding: 0, color: C.soft, fontSize: 13, cursor: 'pointer', textAlign: 'left',
        }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>‹</span>
          {path.slice(0, -1).map(n => n.number ? `${n.number}` : displayTitle(n)).join(' › ')}
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: C.ink }}>
          {node.number ? `${node.number} ` : ''}{displayTitle(node)}
        </span>
        {node.id === focusId && path.length > 1 && <Badge kind="ok">You're here</Badge>}
      </div>
      <div style={{ fontSize: 13, color: C.soft, marginTop: 2 }}>
        {node.workflow_name} · {prog.done} of {prog.total} steps
        {parent && node.hold_stage_id ? ` · holds step ${(parent.stages.find(s => s.id === node.hold_stage_id) || {}).step || ''}` : ''}
      </div>
      <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden', margin: '8px 0 12px' }}>
        <div style={{ width: `${prog.pct}%`, height: '100%', background: C.done }} />
      </div>

      {attention.length > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 10px', marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: C.soft, marginBottom: 6 }}>Needs attention</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {attention.map(({ node: n }) => {
              const d = dueState(n);
              return (
                <button key={n.id} type="button" onClick={() => zoomTo(n)} style={{
                  ...badgeStyle(d.kind), border: 'none', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                  {n.number} {d.long.toLowerCase()}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!showDone && doneRows.length > 0 && (
        <button type="button" onClick={() => setShowDone(true)} style={foldBtn}>
          ▸ {doneRows.length} finished step{doneRows.length === 1 ? '' : 's'} hidden
        </button>
      )}

      {rows.map((row, i) => {
        if (hidden.has(row.id)) return null;
        const waiting = waitingOn(node, row.id);
        return (
          <div key={row.id}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, borderBottom: `1px solid ${C.border}`,
              fontSize: 14, fontWeight: row.status === 'current' ? 700 : 400,
              color: row.status === 'upcoming' || row.status === 'skipped' ? C.soft : C.ink,
            }}>
              <StepDot status={row.status} finish={row === finish} size={14} />
              <span style={{ flex: 1 }}>
                {row.step} · {row.name}
                {waiting.length > 0 && row.status !== 'done' && (
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: C.soonFg }}>🔒 Waiting on {waiting.join(', ')}</span>
                )}
                {row === finish && others.length > 0 && (
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: C.muted }}>or {others.join(' · ')}</span>
                )}
              </span>
              {row.status === 'current' && <Badge kind={dueState(node).kind}>{dueState(node).short}</Badge>}
            </div>
            <PhoneKids kids={kidsOf(row.id)} parent={node} onZoom={zoom} />
          </div>
        );
      })}

      {!showUpcoming && upcomingRows.length > 0 && (
        <button type="button" onClick={() => setShowUpcoming(true)} style={foldBtn}>
          ▸ {upcomingRows.length} upcoming step{upcomingRows.length === 1 ? '' : 's'}
          {upcomingKids > 0 ? ` · ${upcomingKids} sub-task${upcomingKids === 1 ? '' : 's'} later` : ''}
        </button>
      )}

      {!node.planned && (
        <button type="button" className="m-btn m-btn-secondary" onClick={() => onOpenItem(node.id)}
          style={{ marginTop: 14, width: '100%' }}>
          Open {node.workflow_name}
        </button>
      )}
    </div>
  );
}

const foldBtn = {
  display: 'block', width: '100%', minHeight: 40, background: 'none', border: 'none', textAlign: 'left',
  fontSize: 13, color: C.soft, cursor: 'pointer', padding: '4px 0',
};

// ── Public component ───────────────────────────────────────────────────────

export default function WorkflowTree({ tree, onOpenItem }) {
  const isMobile = useIsMobile();
  if (!tree || !tree.root) return null;
  const { root, focus_id: focusId } = tree;
  const prog = progress(root);
  const allNodes = flatten(root);
  const subTotal = allNodes.length - 1;
  const subDone = allNodes.filter(({ node }) => node !== root && node.completed_at).length;
  const finishWaiting = (() => {
    const { finish } = mainPath(root);
    return finish ? waitingOn(root, finish.id) : [];
  })();

  return (
    <div>
      <style>{CSS}</style>
      {isMobile ? (
        <PhoneTree key={root.id} root={root} focusId={focusId} onOpenItem={onOpenItem} />
      ) : (
        <>
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
            background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 18px', marginBottom: 18,
          }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.ink }}>{displayTitle(root)}</div>
              <div style={{ fontSize: 13, color: C.soft, marginTop: 2 }}>
                {root.workflow_name}
                {root.submitter_name ? ` · Submitted by ${root.submitter_name}` : ''}
                {root.created_at ? ` · ${formatDate(root.created_at, { month: 'short', day: 'numeric' })}` : ''}
              </div>
              {finishWaiting.length > 0 && !root.completed_at && (
                <div style={{ fontSize: 13, color: C.soonFg, marginTop: 4 }}>🔒 Finish waits on {finishWaiting.join(', ')}</div>
              )}
            </div>
            <div style={{ textAlign: 'right', minWidth: 180 }}>
              <div style={{ fontSize: 13, color: C.soft }}>
                {prog.done} of {prog.total} steps{subTotal > 0 ? ` · ${subDone} of ${subTotal} sub-tasks` : ''}
              </div>
              <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden', marginTop: 8 }}>
                <div style={{ width: `${prog.pct}%`, height: '100%', background: C.done }} />
              </div>
            </div>
          </div>
          <DesktopTree root={root} focusId={focusId} onOpenItem={onOpenItem} />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.soft, marginTop: 18, alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StepDot status="done" size={12} /> Done</span>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StepDot status="current" size={12} /> Current step</span>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StepDot status="upcoming" size={12} /> Upcoming</span>
            <span>🔒 Holds a step</span>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 18, height: 12, border: `1.5px dashed ${C.line}`, borderRadius: 3 }} /> Starts later
            </span>
          </div>
        </>
      )}
      {tree.truncated && (
        <p style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
          This tree is very large — only the first part is shown.
        </p>
      )}
    </div>
  );
}
